import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import SimpleTooltip from '../../../tooltips/dsPreset/SimpleTooltip';
import InfoIcon from '../../../Icons/16x16/Info';
import { IconSize } from '../../../Icons/IconWrap';

import formElementStyles from '../../layouts/FormElement/FormElement.scss';
import s from './InputLabelText.scss';

interface Props {
  className?: string;
  /** Will show "*Required" next to the label if true */
  isRequired?: boolean;
  /** Shows an info icon with a tooltip after the required indicator */
  labelTooltip?: string;
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * The text of an `<input />`'s label.
 *
 * NOTE: You still need need to wrap this in an `<InputLabel />` tag! This just provides the necessary styling to the text.
 */
const InputLabelText: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  className,
  isRequired,
  labelTooltip,
  onClick,
}) => {
  const { t } = useTranslation();
  return (
    <span className={c(s['root'], className)} onClick={onClick}>
      {children}{' '}
      {isRequired && (
        <span className={s['required-label']}>
          <span aria-hidden="true" title="Required">
            *
          </span>
          <span
            className={c(
              // we pull in the styles from the parent container
              // so it can use :focus-within to show 'required' when the field is focused
              formElementStyles['visually-hidden-required-text']
            )}
          >
            {t('common.required')}
          </span>
        </span>
      )}
      {labelTooltip && (
        <SimpleTooltip text={labelTooltip}>
          <InfoIcon
            className={s['info-icon']}
            aria-label={labelTooltip}
            size={IconSize.SMALLER}
            disableDefaultColors
          />
        </SimpleTooltip>
      )}
    </span>
  );
};
InputLabelText.displayName = 'InputLabelText';

export default InputLabelText;
